import { useSelector } from 'react-redux';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { useApiMutation } from '@/Components/Hooks/useApiMutation';
import { useApiQuery } from '@/Components/Hooks/useApiQuery';

// Endpoint definitions for the Pontosense radar sensor integration.
const ENDPOINTS = {
  associate: '/hacloud/api/v1/pontosense/associate',
  disassociate: ({ tenantId, homeId }) =>
    `/hacloud/api/v1/pontosense/associate/${encodeURIComponent(tenantId)}/${encodeURIComponent(
      homeId
    )}`,
  sensorDetails: ({ tenantId, sn }) =>
    `/hacloud/api/v1/pontosense/sensors/${encodeURIComponent(tenantId)}/${encodeURIComponent(sn)}`,
  tenant: ({ providerId }) => `/hacloud/api/v1/pontosense/tenant/${encodeURIComponent(providerId)}`,
};

const getAuthContext = (userEventsApi, currentUser) => ({
  basePath: userEventsApi?.apiClient?.basePath,
  bearerToken:
    currentUser?.token ||
    userEventsApi?.apiClient?.defaultHeaders?.Authorization?.replace('Bearer ', ''),
});

const getRequiredBasePath = (basePath) => {
  if (typeof basePath !== 'string' || !basePath.trim()) {
    throw new Error('Pontosense API basePath is not configured');
  }

  return basePath.trim().replace(/\/+$/, '');
};

export const joinApiUrl = (basePath, path) => {
  const normalizedBasePath = getRequiredBasePath(basePath);
  const normalizedPath = `${path || ''}`.replace(/^\/+/, '');

  return new URL(normalizedPath, `${normalizedBasePath}/`).toString();
};

const getRequiredPathParam = (value, label) => {
  if (value === undefined || value === null || `${value}`.trim() === '') {
    throw new Error(`Pontosense ${label} is not configured`);
  }

  return value;
};

const associateDevice = async ({ basePath, bearerToken, payload }) => {
  const response = await fetch(joinApiUrl(basePath, ENDPOINTS.associate), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to associate radar sensor: ${response.status}`);
  }

  return response.json().catch(() => ({}));
};

const disassociateDevice = async ({ basePath, bearerToken, tenantId, homeId }) => {
  const requiredTenantId = getRequiredPathParam(tenantId, 'tenant ID');
  const requiredHomeId = getRequiredPathParam(homeId, 'home ID');

  const response = await fetch(
    joinApiUrl(
      basePath,
      ENDPOINTS.disassociate({
        tenantId: requiredTenantId,
        homeId: requiredHomeId,
      })
    ),
    {
      method: 'DELETE',
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to unpair radar sensor: ${response.status}`);
  }

  return response.json().catch(() => ({}));
};

const getSensorDetails = async ({ basePath, bearerToken, tenantId, sn }) => {
  const requiredTenantId = getRequiredPathParam(tenantId, 'tenant ID');
  const requiredSn = getRequiredPathParam(sn, 'serial number');

  const response = await fetch(
    joinApiUrl(basePath, ENDPOINTS.sensorDetails({ tenantId: requiredTenantId, sn: requiredSn })),
    {
      method: 'GET',
      headers: {
        accept: '*/*',
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch radar sensor details: ${response.status}`);
  }

  return response.json().catch(() => ({}));
};

const getTenant = async ({ basePath, bearerToken, providerId }) => {
  const requiredProviderId = getRequiredPathParam(providerId, 'provider ID');

  const response = await fetch(
    joinApiUrl(basePath, ENDPOINTS.tenant({ providerId: requiredProviderId })),
    {
      method: 'GET',
      headers: {
        accept: '*/*',
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch pontosense tenant: ${response.status}`);
  }

  return response.json().catch(() => ({}));
};

// The pontosense API client: plain endpoint definitions, callable outside of React.
export const pontosenseApi = {
  associateDevice,
  disassociateDevice,
  getSensorDetails,
  getTenant,
};

// Auto-generated-style hook (mirrors RTK Query's `use<Endpoint>Mutation` pattern)
// built on the project's existing @tanstack/react-query integration.
export const useAssociateDeviceMutation = (options = {}) => {
  const currentUser = useSelector(getCurrentUser);
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);

  return useApiMutation(async (payload) => {
    const { basePath, bearerToken } = getAuthContext(userEventsApi, currentUser);
    return pontosenseApi.associateDevice({ basePath, bearerToken, payload });
  }, options);
};

export const useDisassociateDeviceMutation = (options = {}) => {
  const currentUser = useSelector(getCurrentUser);
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);

  return useApiMutation(async ({ tenantId, homeId }) => {
    const { basePath, bearerToken } = getAuthContext(userEventsApi, currentUser);
    return pontosenseApi.disassociateDevice({ basePath, bearerToken, tenantId, homeId });
  }, options);
};

export const useGetSensorDetailsMutation = (options = {}) => {
  const currentUser = useSelector(getCurrentUser);
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);

  return useApiMutation(async ({ tenantId, sn }) => {
    const { basePath, bearerToken } = getAuthContext(userEventsApi, currentUser);
    return pontosenseApi.getSensorDetails({ basePath, bearerToken, tenantId, sn });
  }, options);
};

export const useGetPontosenseTenantIdQuery = (providerId, options = {}) => {
  const currentUser = useSelector(getCurrentUser);
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);

  return useApiQuery(
    ['pontosenseTenant', providerId],
    async () => {
      const { basePath, bearerToken } = getAuthContext(userEventsApi, currentUser);
      const data = await pontosenseApi.getTenant({ basePath, bearerToken, providerId });
      return data?.pontosense_tenant_id;
    },
    { enabled: Boolean(providerId), ...options }
  );
};
